/**
 * API Contract Definitions
 *
 * Declarative contracts that define when each API call should fire.
 * Each contract specifies:
 * - prerequisites: store fields that must be truthy
 * - dependsOn: other contracts that must succeed first
 * - conditions: runtime checks against store state
 * - executor: the function that performs the API call
 * - trackFulfillment: whether to prevent re-firing once succeeded
 *
 * Contracts are evaluated on every step submit. The orchestrator
 * checks all unfulfilled contracts and fires any whose prerequisites
 * and conditions are newly satisfied.
 */
import { apiDateFormat } from "@chhjit/react-components";
import moment from "moment-timezone";

import type { StoreName } from "./store-accessors";
import { readStorePath } from "./store-accessors";
import { CategoryIdEnum } from "../../types";
import { $account } from "../../../entities/account";
import { $quoteCalculation } from "../../../entities/quote";
import { $manifestCapabilities } from "./manifest-capabilities";

export type StoreSnapshot = Record<StoreName, unknown>;

export type ApiContractPrerequisite = {
  store: StoreName;
  path: string;
};

export type ApiContract = {
  id: string;
  /** All must be truthy for the contract to be eligible */
  prerequisites: ApiContractPrerequisite[];
  /** Other contract IDs that must have succeeded first */
  dependsOn?: string[];
  /** Additional runtime condition (e.g., category check) */
  condition?: () => boolean;
  /** The executor — called when all prerequisites + conditions are met */
  executor: () => Promise<unknown>;
  /** Once fulfilled, don't re-fire on subsequent step submits */
  trackFulfillment: boolean;
  /** Human-readable description for debugging */
  description: string;
};

// ──────────────────────────────────────────────
// Prerequisite check helper
// ──────────────────────────────────────────────

const isTruthy = (storeName: StoreName, path: string): boolean => {
  const val = readStorePath(storeName, path);
  return val != null && val !== "" && val !== false && val !== 0;
};

export const checkPrerequisites = (
  prerequisites: ApiContractPrerequisite[],
): boolean => {
  return prerequisites.every((p) => isTruthy(p.store, p.path));
};

// ──────────────────────────────────────────────
// Category helpers
// ──────────────────────────────────────────────

const getCategoryId = (): number | undefined =>
  readStorePath("account", "category.id") as number | undefined;

const isJunk = () => getCategoryId() === CategoryIdEnum.Junk;
const isMoving = () => getCategoryId() === CategoryIdEnum.Moving;
const isLabor = () => getCategoryId() === CategoryIdEnum.Labor;

const getAccountId = () => readStorePath("account", "id") as number | undefined;
const getLocationId = () =>
  readStorePath("serviceLocation", "id") as number | undefined;
const getAccountLocationId = () =>
  readStorePath("account", "location.id") as number | undefined;
const getBookingType = () =>
  readStorePath("bookingProcess", "bookingType") as string | undefined;
const getDwellingType = () =>
  readStorePath("quote", "origin.dwellingType.id") as number | undefined;
const getDestinationDwellingType = () =>
  readStorePath("quote", "destination.dwellingType.id") as number | undefined;
const getContactConfirmed = () =>
  readStorePath("appointment", "contactConfirmed") as boolean | undefined;
const getCallRequestConfirmed = () =>
  readStorePath("appointment", "callRequestConfirmed") as boolean | undefined;
const getLeadId = () => $account.getState().lead?.id;

// Moving quoteCalculate result — see the OSE availability plan.
const getOnSiteEstimateRequired = () =>
  $quoteCalculation.getState()?.onSiteEstimateRequired;
const getInterstateDestinationUnserviced = () =>
  $quoteCalculation.getState()?.interstateDestinationUnserviced;
// Set by checkAvailabilityRange — only meaningful once that contract has
// fired (OSE required, target date isn't today).
const getHasAvailabilityInRange = () =>
  $quoteCalculation.getState()?.hasAvailabilityInRange;
// Whether the ACTIVE workflow manifest can actually render the OSE
// availability page (only the on-site-estimate Sanity variant carries the
// step). When false — control bucket of the on-site-ose experiment, or a
// Sanity outage on a build whose cached fallback was stripped — the Moving
// contracts below fall back to the OLD flow (quoteCreate + callRequest →
// standard confirmation) even when calculate said an OSE is required.
const movingHasAvailabilityPage = () =>
  $manifestCapabilities.getState().movingHasAvailabilityStep;

/**
 * Is the selected target date "today" in the service location's own
 * timezone? Mirrors calculate.post.php's own same-day check (which compares
 * against `now` in the franchise's timezone, not the browser's).
 */
export const isTargetDateToday = (): boolean => {
  const dateStr = readStorePath("quote", "date") as string | undefined;
  if (!dateStr) return false;
  const timeZoneName = readStorePath("serviceLocation", "timeZone.name") as
    | string
    | undefined;
  const today = timeZoneName ? moment().tz(timeZoneName) : moment();
  return moment(dateStr).format(apiDateFormat) === today.format(apiDateFormat);
};

// ──────────────────────────────────────────────
// Contract registry
// ──────────────────────────────────────────────

/**
 * Build the contract list. This is a function (not a constant) because
 * executors need to be supplied by the caller (they depend on React hooks
 * like useAlert and useNavigate which aren't available at module scope).
 */
export type ContractExecutors = {
  serviceCheck: () => Promise<unknown>;
  accountCreate: () => Promise<unknown>;
  accountUpdate: () => Promise<unknown>;
  leadUpdate: () => Promise<unknown>;
  leadVolume: () => Promise<unknown>;
  appointmentCreate: () => Promise<unknown>;
  quoteCalculate: () => Promise<unknown>;
  checkAvailabilityRange: () => Promise<unknown>;
  quoteCreate: (options?: {
    afterQuoteCreated?: () => Promise<void>;
  }) => Promise<unknown>;
  callRequest: () => Promise<unknown>;
  markLeadLost: () => Promise<unknown>;
  sendSameDayDispatchMessage: () => Promise<unknown>;
  sendNoAvailabilityDispatchMessage: () => Promise<unknown>;
  accountPing: () => Promise<unknown>;
  leadInProgress: () => Promise<unknown>;
};

// Shared by quoteCalculate + quoteCreate — both need the full quote shape
// filled in before they can fire.
const MOVING_QUOTE_PREREQUISITES: ApiContractPrerequisite[] = [
  { store: "account", path: "id" },
  { store: "serviceLocation", path: "id" },
  { store: "quote", path: "rooms" },
  { store: "quote", path: "origin.dwellingType" },
  { store: "quote", path: "origin.floor" },
  { store: "quote", path: "destination.dwellingType" },
  { store: "quote", path: "destination.floor" },
  { store: "quote", path: "date" },
  // Explicit user confirmation — set by the hidden bookingConfirmed field.
  // Prevents calculate/create from firing until the user reaches the
  // additional-factors step (where the hidden field lives). Move the hidden
  // field to any step to change when this fires.
  { store: "appointment", path: "bookingConfirmed" },
];

export const buildContracts = (executors: ContractExecutors): ApiContract[] => [
  // ─── serviceCheck ───
  {
    id: "serviceCheck",
    description: "Validate postal code is in service area",
    prerequisites: [
      { store: "account", path: "category.id" },
      { store: "account", path: "billingAddress.postal" },
    ],
    executor: executors.serviceCheck,
    trackFulfillment: true,
  },

  // ─── accountCreate ───
  {
    id: "accountCreate",
    description: "Create account with billing info + contact details",
    prerequisites: [
      { store: "account", path: "firstName" },
      { store: "account", path: "lastName" },
      { store: "account", path: "phone" },
      { store: "account", path: "email" },
      // Only postal is required — city/state/country are set by postal lookup,
      // and full street address can be filled in later on pick-up-location.
      { store: "account", path: "billingAddress.postal" },
      { store: "account", path: "category.id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["serviceCheck"],
    // For Labor, also require contactConfirmed — a hidden field set only when
    // the user submits contact-information. This prevents accountCreate from
    // firing at pick-up-location when contact info from a prior flow persists
    // in the store (all other prerequisites would already be satisfied the
    // moment serviceCheck resolves).
    condition: () =>
      !getAccountId() && (isLabor() ? !!getContactConfirmed() : true),
    executor: executors.accountCreate,
    trackFulfillment: true,
  },

  // ─── accountUpdate ───
  {
    id: "accountUpdate",
    description: "Update existing account at the same location",
    prerequisites: [
      { store: "account", path: "firstName" },
      { store: "account", path: "lastName" },
      { store: "account", path: "phone" },
      { store: "account", path: "email" },
      { store: "account", path: "billingAddress.postal" },
      { store: "account", path: "category.id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["serviceCheck"],
    condition: () => {
      const accountId = getAccountId();
      const accountLocationId = getAccountLocationId();
      const locationId = getLocationId();
      return !!accountId && accountLocationId === locationId;
    },
    executor: executors.accountUpdate,
    trackFulfillment: false, // can re-fire if data changes
  },

  // ─── leadUpdate ───
  {
    id: "leadUpdate",
    description: "Update lead target date on Moving target-date step",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
      { store: "quote", path: "date" },
    ],
    dependsOn: ["serviceCheck"],
    condition: () => isMoving() && !!getLeadId(),
    executor: executors.leadUpdate,
    trackFulfillment: false,
  },

  // ─── leadVolume ───
  // CID-7928. First fires on the kind-of-items submit, which is when field bindings copy
  // typeOfJunk into the appointment store. Not trackFulfillment: a customer who comes back
  // and picks another tile must be saved again. The re-evaluation on every later step is
  // absorbed by the executor, which skips a value the server already confirmed. Leaving
  // Junk simply stops it firing — the stored value is kept, never cleared.
  {
    id: "leadVolume",
    description: "Save the Junk item volume on the lead (account_lead_meta)",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
      { store: "appointment", path: "notes.typeOfJunk" },
    ],
    dependsOn: ["serviceCheck"],
    condition: () => isJunk() && !!getLeadId(),
    executor: executors.leadVolume,
    trackFulfillment: false,
  },

  // ─── appointmentCreate ───
  {
    id: "appointmentCreate",
    description: "Create junk removal appointment",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
      { store: "appointment", path: "startDate" },
      { store: "appointment", path: "endDate" },
      { store: "appointment", path: "resource.id" },
      // Explicit user confirmation — set by the hidden bookingConfirmed field.
      // Prevents appointment creation until the user clicks Book on the
      // confirmation step (truck-loads by default). Move the hidden field to
      // any step to change when booking fires.
      { store: "appointment", path: "bookingConfirmed" },
    ],
    // Depends on serviceCheck (not accountCreate) so this also fires for
    // returning users whose account already exists and accountCreate is skipped.
    // The account.id prerequisite above ensures the account exists.
    dependsOn: ["serviceCheck"],
    condition: isJunk,
    executor: executors.appointmentCreate,
    trackFulfillment: true,
  },

  // ─── quoteCalculate ───
  // Fires at Special Items submit for Moving. Stores the OSE/call/interstate
  // flags ($quoteCalculation) that quoteCreate/callRequest below and
  // branching-rules read to decide what happens next — see the OSE
  // availability plan.
  //
  // trackFulfillment: false is deliberate — unlike accountCreate/
  // appointmentCreate/quoteCreate/callRequest (which create a real backend
  // resource and must never fire twice), calculate is a pure read/compute
  // with no side effects. It should refire every time this step is
  // submitted, so a user who goes back and changes the date/bedrooms/address
  // after already calculating once gets a fresh result instead of a stale
  // cached one.
  {
    id: "quoteCalculate",
    description: "Calculate Moving quote and resolve OSE/interstate flags",
    prerequisites: MOVING_QUOTE_PREREQUISITES,
    dependsOn: ["serviceCheck"],
    condition: isMoving,
    executor: executors.quoteCalculate,
    trackFulfillment: false,
  },

  // ─── checkAvailabilityRange ───
  // Only relevant when calculate says OSE is required and the target date
  // isn't today (the one case that would otherwise land on the availability
  // step) — determines whether there's any slot at all before the target
  // date, or whether we should skip straight to the no-availability dispatch
  // branch instead. Same trackFulfillment:false rationale as quoteCalculate:
  // a pure read that must refire whenever the user resubmits this step with
  // different data. See the OSE availability plan.
  {
    id: "checkAvailabilityRange",
    description: "Check for any EST slot before the target date",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
      { store: "quote", path: "date" },
    ],
    dependsOn: ["serviceCheck", "quoteCalculate"],
    condition: () =>
      isMoving() &&
      // No availability page in the active workflow → old flow; the range
      // pre-check would be dead weight (two API calls feeding a decision
      // that can't be acted on).
      movingHasAvailabilityPage() &&
      !getInterstateDestinationUnserviced() &&
      getOnSiteEstimateRequired() === true &&
      !isTargetDateToday(),
    executor: executors.checkAvailabilityRange,
    trackFulfillment: false,
  },

  // ─── quoteCreate ───
  // Fires immediately after Special Items in three cases: no on-site estimate
  // needed (old flow); an on-site estimate is needed but the target date is
  // today (same-day); or an on-site estimate is needed, not same-day, but
  // checkAvailabilityRange found no slot before the target date
  // (no-availability). All three create the quote right away with a dispatch
  // message to FP instead of a picked appointment time, and all three
  // require the destination not be interstate-unserviced. Otherwise (OSE
  // required, not same-day, slots exist), quote creation is deferred until
  // slot-pick on the availability step — see the OSE availability plan.
  {
    id: "quoteCreate",
    description:
      "Create moving quote immediately (no OSE needed, same-day OSE, or no-availability OSE)",
    prerequisites: MOVING_QUOTE_PREREQUISITES,
    dependsOn: ["serviceCheck", "quoteCalculate"],
    condition: () =>
      isMoving() &&
      !getInterstateDestinationUnserviced() &&
      (getOnSiteEstimateRequired() === false ||
        isTargetDateToday() ||
        getHasAvailabilityInRange() === false ||
        // OSE required but no availability page in the active workflow
        // (control bucket / Sanity outage) — quote creation must NOT stay
        // deferred to a slot-pick that can never happen: create it now, old
        // flow (callRequest below fires too, dispatches don't).
        !movingHasAvailabilityPage()),
    executor: () => executors.quoteCreate(),
    trackFulfillment: true,
  },

  // ─── callRequest ───
  {
    id: "callRequest",
    description:
      "Send call request (Labor, business dwelling, call booking type, or Moving with no on-site estimate needed)",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    // Depends on serviceCheck (not accountCreate) — same rationale as appointmentCreate.
    dependsOn: ["serviceCheck"],
    condition: () => {
      // For Labor, require contactConfirmed — a hidden field set only when the
      // user submits contact-information. This prevents callRequest from firing
      // at pick-up-location when account.id persists from a prior booking session
      // (all other prerequisites would already be satisfied the moment
      // serviceCheck resolves, mirroring the accountCreate guard above).
      if (isLabor()) return !!getContactConfirmed();
      if (getBookingType() === "call") return true;
      if (Number(getDwellingType()) === 6) return true; // Business origin = 6
      if (Number(getDestinationDwellingType()) === 6) return true; // Business destination = 6
      // Moving: fires alongside quoteCreate when calculate determined no
      // on-site estimate is required (old flow) — see the OSE availability
      // plan. Relies on quoteCalculate having already run earlier in this
      // same evaluation pass (array order above). Also fires when an OSE IS
      // required but the active workflow has no availability page (control
      // bucket / Sanity outage): the old flow's call request is the
      // fallback — the OSE dispatch messages are treatment-only wording.
      if (isMoving()) {
        return (
          !getInterstateDestinationUnserviced() &&
          (getOnSiteEstimateRequired() === false ||
            (getOnSiteEstimateRequired() === true &&
              !movingHasAvailabilityPage()))
        );
      }
      if (getCallRequestConfirmed()) return true; // CMS-placed hidden field trigger
      return false;
    },
    executor: executors.callRequest,
    trackFulfillment: true,
  },

  // ─── markLeadLost ───
  // Moving, interstate-destination-unserviced: mark the lead lost (Un-Serviced
  // Zip Code) instead of creating a quote or sending a call request — same
  // endpoint ABT/MET uses. See the OSE availability plan.
  {
    id: "markLeadLost",
    description: "Mark lead lost — interstate destination unserviced",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["serviceCheck", "quoteCalculate"],
    condition: () =>
      isMoving() && getInterstateDestinationUnserviced() === true,
    executor: executors.markLeadLost,
    trackFulfillment: true,
  },

  // ─── sendSameDayDispatchMessage ───
  // Moving, OSE required but target date is today: quote was just created
  // above (no slot to pick for a same-day request), so notify FP via
  // dispatch message instead of sending a call request. See the OSE
  // availability plan.
  {
    id: "sendSameDayDispatchMessage",
    description: "Send same-day OSE dispatch message to FP",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["serviceCheck", "quoteCalculate", "quoteCreate"],
    condition: () =>
      isMoving() &&
      // Treatment-only wording: without the availability page the old
      // flow's callRequest is the fallback, not an OSE dispatch.
      movingHasAvailabilityPage() &&
      !getInterstateDestinationUnserviced() &&
      getOnSiteEstimateRequired() === true &&
      isTargetDateToday(),
    executor: executors.sendSameDayDispatchMessage,
    trackFulfillment: true,
  },

  // ─── sendNoAvailabilityDispatchMessage ───
  // Moving, OSE required, not same-day, but checkAvailabilityRange found no
  // slot before the target date: quote was just created above (same as
  // same-day — no slot to pick), so notify FP via the no-availability
  // dispatch message instead. See the OSE availability plan.
  {
    id: "sendNoAvailabilityDispatchMessage",
    description: "Send no-availability-in-range OSE dispatch message to FP",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["serviceCheck", "quoteCalculate", "quoteCreate"],
    condition: () =>
      isMoving() &&
      // Treatment-only wording — same rationale as sendSameDayDispatchMessage.
      movingHasAvailabilityPage() &&
      !getInterstateDestinationUnserviced() &&
      getOnSiteEstimateRequired() === true &&
      !isTargetDateToday() &&
      getHasAvailabilityInRange() === false,
    executor: executors.sendNoAvailabilityDispatchMessage,
    trackFulfillment: true,
  },

  // ─── accountPing after callRequest ───
  {
    id: "accountPingCallRequest",
    description: "Ping account as booking complete after call request",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["callRequest"],
    executor: executors.accountPing,
    trackFulfillment: true,
  },

  // ─── accountPing after appointmentCreate ───
  {
    id: "accountPingAppointment",
    description: "Ping account as booking complete after appointment creation",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["appointmentCreate"],
    executor: executors.accountPing,
    trackFulfillment: true,
  },

  // ─── accountPing after markLeadLost ───
  {
    id: "accountPingLeadLost",
    description: "Ping account as booking complete after marking lead lost",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["markLeadLost"],
    executor: executors.accountPing,
    trackFulfillment: true,
  },

  // ─── accountPing after sendSameDayDispatchMessage ───
  {
    id: "accountPingSameDayDispatch",
    description:
      "Ping account as booking complete after same-day OSE dispatch message",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["sendSameDayDispatchMessage"],
    executor: executors.accountPing,
    trackFulfillment: true,
  },

  // ─── accountPing after sendNoAvailabilityDispatchMessage ───
  {
    id: "accountPingNoAvailability",
    description:
      "Ping account as booking complete after no-availability OSE dispatch message",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["sendNoAvailabilityDispatchMessage"],
    executor: executors.accountPing,
    trackFulfillment: true,
  },

  // ─── leadInProgress ───
  // CID-7883: the lead is seeded with the "Abandoned online booking" no-sale reason at the
  // start of the funnel. A customer who finishes without booking — call request or either
  // dispatch message — moves it to "In Progress". One contract per terminal outcome, mirroring
  // the accountPing family above — the outcomes are mutually exclusive, so they cannot share a
  // single dependsOn.
  //
  // appointmentCreate is deliberately absent: the appointment POST clears the reason
  // server-side for every booking. So is markLeadLost: on the interstate not-serviced page the
  // flow has already recorded "Un-Serviced Zip Code" (reason 5), which must survive. Both
  // exceptions are expressed by the missing contract rather than by a condition.
  {
    id: "leadInProgressCallRequest",
    description: "Mark the lead In Progress after call request",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["callRequest"],
    executor: executors.leadInProgress,
    trackFulfillment: true,
  },

  {
    id: "leadInProgressSameDayDispatch",
    description:
      "Mark the lead In Progress after same-day OSE dispatch message",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["sendSameDayDispatchMessage"],
    executor: executors.leadInProgress,
    trackFulfillment: true,
  },

  {
    id: "leadInProgressNoAvailability",
    description:
      "Mark the lead In Progress after no-availability OSE dispatch message",
    prerequisites: [
      { store: "account", path: "id" },
      { store: "serviceLocation", path: "id" },
    ],
    dependsOn: ["sendNoAvailabilityDispatchMessage"],
    executor: executors.leadInProgress,
    trackFulfillment: true,
  },
];
